import cn from 'classnames'
import dynamic from 'next/dynamic'
import React, { memo, useCallback, useEffect } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
// import { ArticleCart } from 'src/components/ArticleCart/ArticleCart'
// import { CompareModalContent } from 'src/components/CompareModalContent/CompareModalContent'
import Drawer from 'src/components/global/Drawer/Drawer'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import SkeletonLoading from 'src/components/SkeletonLoading/SkeletonLoading'
// import { Section } from 'src/components/global/Overview/Section'
// import { SimilarSolutions } from 'src/components/SimilarSolutions/SimilarSolutions'
import { SolutionFounderItem } from 'src/components/SolutionFounderItem/SolutionFounderItem'
import { PRACTICE_AREAS_URL, THEME } from 'src/config/config'
import {
  FOUNDERS_EXECUTIVE_TEAM,
  FUNDING_DETAILS,
  fundingAmountsC,
  fundingInfosC,
  OPEN_TO_INVESTMENT,
} from 'src/constants/constants'
import {
  IProductSolutionDataFragment,
  ISimilarSolutionItemFragment,
  useGetProductSolutionSecurityDataLazyQuery,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { AppState } from 'src/store/types'
// import { CompareInfos } from 'src/store/types/compare'
import { FeaturesType, SectionItem } from 'src/types'
import { checkNotEmpty } from 'src/utils/checkNotEmpty'
import { getFeaturesItems } from 'src/utils/getFeaturesItems'
import { getYearFoundedItem } from 'src/utils/getYearFoundedItem'
import { canViewPaywalledSolutionFields } from 'src/utils/solutionPaywall'

import IconBusiness from '../../../assets/icon_business.svg'
import IconLawFirms from '../../../assets/icon_law_firms.svg'
interface DetailContainerProps {
  className?: string
  vendor: IProductSolutionDataFragment
  similars: ISimilarSolutionItemFragment[]
  relatedContent?: any[]
  totalRelatedContent?: number
}

const Section = dynamic(() => import('src/components/global/Overview/Section').then((mod) => mod.Section), {
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const CompareModalContent = dynamic(
  () => import('src/components/CompareModalContent/CompareModalContent').then((mod) => mod.CompareModalContent),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const ArticleCart = dynamic(() => import('src/components/ArticleCart/ArticleCart').then((mod) => mod.ArticleCart), {
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const SimilarSolutions = dynamic(
  () => import('src/components/SimilarSolutions/SimilarSolutions').then((mod) => mod.SimilarSolutions),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const RelatedContents = dynamic(
  () => import('src/components/Partials/RelatedContents/RelatedContents').then((mod) => mod.RelatedContents),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)

/**
 * Primary UI component for user interaction
 */
export const DetailContainer: React.FC<DetailContainerProps> = memo(
  ({ className, vendor, similars, relatedContent = [], totalRelatedContent = 0 }) => {
    const longDescription = vendor?.longDescription || ''
    const productType = vendor?.productType?.toLocaleLowerCase() || ''
    const consolidationData: any = vendor?.consolidationData || null
    const website = vendor?.website || ''
    const languages = vendor?.languages || []
    const hqs = vendor?.hqs || []
    const offices = vendor?.offices || []
    const regionsServed = vendor?.regionsServed || []
    const features = vendor?.features || []
    const audiences = vendor?.productData?.audiences || []
    const typeOfUser = vendor?.productData?.typeOfUser || []
    const practiceAreas = vendor?.productData?.practiceAreas || []
    const existingCustomers = vendor?.existingCustomers || []
    const descOtherExistingCustomer = vendor?.descOtherExistingCustomer || ''
    const descOtherIntegration = vendor?.descOtherIntegration || ''
    const subTopics = vendor?.subTopics || []
    const yearFounded = vendor?.yearFounded

    // it is an array of attributes
    const attributes = vendor?.productData?.attributes || []
    const founders = vendor?.founders || []
    const valueProposition = vendor?.productData?.valueProposition || ''
    const deployments = vendor?.productData?.deployments || []
    const integrations = vendor?.productData?.integrations || []
    const linkedin = vendor?.linkedin || ''
    const twitter = vendor?.twitter || ''
    const fundingInfo = vendor?.productData?.fundingInfo
    const fundingAmount = vendor?.productData?.fundingAmount
    const [isVisible, setIsVisible] = React.useState(false)
    const closeDrawer = useCallback(() => setIsVisible(false), [])
    // const compareInfos: CompareInfos = useSelector((state: AppState) => state.compareInfos)
    const [detailsItems, setDetailsItems] = React.useState<SectionItem[]>([])
    const [loadContents, { loading: securityLoading, data: securityData }] =
      useGetProductSolutionSecurityDataLazyQuery()

    // const authInfo = useSelector((state: AppState) => state.authInfo)
    const { compareInfos, authInfo } = useSelector(
      (state: AppState) => ({
        compareInfos: state.compareInfos,
        authInfo: state.authInfo,
      }),
      shallowEqual,
    )
    const isDiverseLeadership = vendor?.isDiverseLeadership || false
    const { session } = useAuthenticated()
    const fundingDetails = vendor?.productData?.fundingDetails || null
    const openToInvestment = vendor?.productData?.openToInvestment || false
    const companyType = session?.user?.companyType || ''
    const isPremiumUser = session?.user?.isPremium || false
    const unlockPaywalledContent = canViewPaywalledSolutionFields(!!session?.user?.isPremium, vendor, session?.user?.id)

    let showOpenToInvestment = false

    if (companyType.toLocaleLowerCase() === 'investor') {
      showOpenToInvestment = true
    }

    useEffect(() => {
      if (compareInfos?.solutions?.length > 0) {
        if (compareInfos.solutions.length === 1) {
          // const compare: CompareSolution = compareInfos.solutions[0]
          /* if (compare.slug !== vendor?.slug) {
              setIsVisible(true)
            } else {
              setIsVisible(false)
            } */
          setIsVisible(true)
        } else {
          setIsVisible(true)
        }
      } else {
        setIsVisible(false)
      }
    }, [compareInfos])

    const overviewItems: SectionItem[] = []
    if (checkNotEmpty(hqs)) {
      const convertedItems: FeaturesType[] =
        hqs?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '', iconColor: '#011D58' })) || []
      const item: SectionItem = {
        title: 'HEADQUARTERS',
        type: 'SpanDataIcon',
        subType: 'normal',
        items: convertedItems,
      }
      overviewItems.push(item)
    }
    if (checkNotEmpty(linkedin)) {
      const displayName = vendor?.displayName || 'Company'
      const title = `View ${displayName} on LinkedIn`
      const item: SectionItem = {
        type: 'LinkedinIconLink',
        items: [linkedin!],
        title,
      }
      overviewItems.push(item)
    }
    if (checkNotEmpty(languages)) {
      const convertedItems: FeaturesType[] =
        languages?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
      const item: SectionItem = { title: 'LANGUAGES', type: 'SpanDataList', subType: 'more', items: convertedItems }
      overviewItems.push(item)
    }
    if (checkNotEmpty(offices)) {
      const convertedItems: FeaturesType[] =
        offices?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []

      const item: SectionItem = { title: 'OFFICES', type: 'SpanDataList', subType: 'normal', items: convertedItems }
      overviewItems.push(item)
    }
    if (checkNotEmpty(twitter)) {
      const displayName = vendor?.displayName || 'Company'
      const title = `View ${displayName} on Twitter`
      const item: SectionItem = { type: 'TwitterIconLink', items: [twitter!], title }
      overviewItems.push(item)
    }

    if (checkNotEmpty(yearFounded)) {
      const yearFoundedItem = getYearFoundedItem('YEAR FOUNDED', yearFounded)
      if (yearFoundedItem) {
        overviewItems.push(yearFoundedItem)
      }
    }
    if (checkNotEmpty(regionsServed)) {
      const convertedItems: FeaturesType[] =
        regionsServed?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
      const item: SectionItem = {
        title: 'REGIONS SERVED',
        type: 'SpanDataList',
        subType: 'normal',
        items: convertedItems,
      }
      overviewItems.push(item)
    }

    /* if (checkNotEmpty(founders)) {
    const foundersItem: SectionItem = {
      title: 'Founders',
      type: 'CustomList',
      subType: '',
      items: founders || [],
    }

    overviewItems.push(foundersItem)
  } */

    if (checkNotEmpty(fundingInfo)) {
      const item: SectionItem = {
        title: 'Latest Funding Round',
        type: 'DottedDataList',
        subType: 'more',
        items: [{ name: fundingInfosC.find((item: any) => item.value === fundingInfo)?.label }],
      }
      overviewItems.push(item)
    }

    if (checkNotEmpty(fundingAmount)) {
      const item: SectionItem = {
        title: 'Total Funding Amount',
        type: 'DottedDataList',
        subType: 'more',
        items: [{ name: fundingAmountsC.find((item: any) => item.value === fundingAmount)?.label }],
      }
      overviewItems.push(item)
    }

    const featuresItems: SectionItem[] = getFeaturesItems(
      subTopics.filter((subTopic): subTopic is NonNullable<typeof subTopic> => subTopic !== null),
      features.filter((feature): feature is NonNullable<typeof feature> => feature !== null),
    ).filter((obj) => obj.items.some((item) => item.isChecked))
    const randomNum = Math.floor(Math.random() * 1000) // Generates a number between 0 and 999

    const areasItems: SectionItem[] = []
    if (checkNotEmpty(audiences)) {
      const convertedItems: FeaturesType[] =
        (audiences &&
          audiences?.map((item) => {
            const name = item?.name || ''
            let icon = ''
            if (name === 'Law Firms') icon = IconLawFirms
            else if (name === 'Corporate Legal') icon = IconBusiness
            const converted: FeaturesType = { name: name, icon: icon }
            return converted
          })) ||
        []
      const item: SectionItem = {
        title: 'TARGET AUDIENCE',
        type: 'SpanDataList',
        subType: 'cart',
        items: convertedItems,
        isFullWidth: true,
      }
      areasItems.push(item)
    }
    if (checkNotEmpty(typeOfUser)) {
      const convertedItems: FeaturesType[] =
        typeOfUser?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
      const item: SectionItem = {
        title: 'TYPE OF USER',
        type: 'DottedDataList',
        subType: '',
        items: convertedItems,
        isFullWidth: true,
        parentGridCols: 1,
        childGridCols: 1,
      }
      areasItems.push(item)
    }
    if (checkNotEmpty(existingCustomers)) {
      const items: FeaturesType[] =
        existingCustomers
          ?.filter((item) => !!item?.name)
          .map((item) => ({
            name: item?.name || '',
            ...(item?.name.toLowerCase() === 'others' && { name: descOtherExistingCustomer }),
          })) || []
      const item: SectionItem = {
        title: 'EXISTING CUSTOMERS',
        type: 'SpanDataList',
        subType: 'normal',
        items,
      }
      areasItems.push(item)
    }
    if (checkNotEmpty(practiceAreas)) {
      const convertedItems: FeaturesType[] =
        practiceAreas
          ?.filter((item) => !!item?.name)
          .map((item) => ({ name: item?.name || '', id: item?.documentId || '' })) || []

      const case_1: SectionItem = {
        title: 'PRACTICE AREAS',
        type: 'SpanDataList',
        subType: 'cart',
        items: convertedItems,
        isFullWidth: true,
      }
      const case_2: SectionItem = {
        title: 'PRACTICE AREAS',
        type: 'DottedDataList',
        subType: '',
        items: convertedItems,
      }
      const item: SectionItem =
        THEME === 'legaltech' && PRACTICE_AREAS_URL !== 'false' && !!PRACTICE_AREAS_URL ? case_1 : case_2

      areasItems.push(item)
    }
    if (checkNotEmpty(valueProposition)) {
      const item: SectionItem = {
        title: 'Value Proposition',
        type: 'DottedDataList',
        subType: '',
        items: valueProposition ? [{ name: valueProposition }] : [],
        isFullWidth: true,
      }
      areasItems.push(item)
    }
    if (checkNotEmpty(fundingDetails)) {
      const item: SectionItem = {
        title: FUNDING_DETAILS,
        type: 'string',
        subType: '',
        items: [],
        sValue: fundingDetails || '',
        isFullWidth: true,
      }
      overviewItems.push(item)
    }

    if (
      THEME === 'legaltech' &&
      openToInvestment &&
      ((showOpenToInvestment && isPremiumUser) || canViewPaywalledSolutionFields(false, vendor, session?.user?.id))
    ) {
      const item: SectionItem = {
        title: 'OPEN_TO_INVESTMENT',
        type: 'CheckDataList',
        isBold: true,
        isFullWidth: true,
        items: [{ name: OPEN_TO_INVESTMENT, isChecked: true }],
      }
      overviewItems.push(item)
    }

    useEffect(() => {
      const _detailsItems: SectionItem[] = []
      if (checkNotEmpty(subTopics) && !detailsItems.some((item) => item.title === 'PRODUCT TYPES')) {
        const convertedItems: FeaturesType[] =
          subTopics?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
        const item: SectionItem = {
          title: 'PRODUCT TYPES',
          type: 'SpanDataList',
          subType: 'cart',
          items: convertedItems,
          isFullWidth: true,
        }
        _detailsItems.push(item)
      }
      if (checkNotEmpty(attributes) && !detailsItems.some((item) => item.title === 'ATTRIBUTES')) {
        const convertedItems: FeaturesType[] =
          attributes?.filter((item: any) => !!item?.name).map((item: any) => ({ name: item?.name || '' })) || []
        const item: SectionItem = {
          title: 'ATTRIBUTES',
          type: 'DottedDataList',
          subType: '',
          items: convertedItems,
          isFullWidth: true,
          colValue: 2,
          parentGridCols: 1,
          childGridCols: 2,
        }
        _detailsItems.push(item)
      }
      if (checkNotEmpty(founders)) {
        const item: SectionItem = {
          title: FOUNDERS_EXECUTIVE_TEAM, // 'Founders/Executive Team',
          type: 'CustomList',
          subType: '',
          isFullWidth: true,
          items: founders || [],
          isDiverseLeadership: isDiverseLeadership,
        }
        _detailsItems.push(item)
      }

      if (checkNotEmpty(deployments) && !detailsItems.some((item) => item.title === 'DEPLOYMENT')) {
        const convertedItems: FeaturesType[] =
          deployments?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
        const item: SectionItem = {
          title: 'DEPLOYMENT',
          type: 'DottedDataList',
          subType: '',
          items: convertedItems,
          parentGridCols: 1,
          childGridCols: 1,
          colValue: 2,
        }
        _detailsItems.push(item)
      }
      if (checkNotEmpty(integrations) && !detailsItems.some((item) => item.title === 'INTEGRATIONS')) {
        const convertedItems: FeaturesType[] =
          integrations
            ?.filter((item) => !!item?.name)
            .flatMap((item) => {
              if (item?.name.toLowerCase() === 'others' && descOtherIntegration) {
                return descOtherIntegration.split(',').map((name) => ({ name: name.trim() }))
              }
              return { name: item?.name || '' }
            }) || []
        const item: SectionItem = {
          title: 'INTEGRATIONS',
          type: 'DottedDataList',
          subType: '',
          items: convertedItems,
          parentGridCols: 2,
          childGridCols: 1,
        }
        _detailsItems.push(item)
      }

      if (_detailsItems.length > 0) {
        setDetailsItems(_detailsItems)
      }
    }, [vendor])

    useEffect(() => {
      const _detailsItems: SectionItem[] = []
      const _securityData = securityData?.solutions?.[0]?.productData?.security
      if (
        _securityData !== null &&
        _securityData !== undefined &&
        _securityData?.length > 0 &&
        !detailsItems.some((item) => item.title === 'Security')
      ) {
        if (_securityData?.length > 0) {
          const convertedItems: FeaturesType[] =
            _securityData?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
          const item: SectionItem = { title: 'Security', type: 'DottedDataList', subType: '', items: convertedItems }
          _detailsItems.push(item)
        } else {
          const item: SectionItem = {
            title: 'Security',
            type: 'DottedDataList',
            subType: '',
            items: [
              {
                name: 'Security',
              },
            ],
          }
          _detailsItems.push(item)
        }
      }
      const _hostingLocationsData = securityData?.solutions?.[0]?.productData?.dataHostingLocations
      if (
        _hostingLocationsData !== null &&
        _hostingLocationsData !== undefined &&
        _hostingLocationsData?.length > 0 &&
        !detailsItems.some((item) => item.title === 'Data Hosting Locations')
      ) {
        if (_hostingLocationsData?.length > 0) {
          const convertedItems: FeaturesType[] =
            _hostingLocationsData?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
          const item: SectionItem = {
            title: 'Data Hosting Locations',
            type: 'DottedDataList',
            subType: '',
            items: convertedItems,
          }
          _detailsItems.push(item)
        } else {
          const item: SectionItem = {
            title: 'Data Hosting Locations',
            type: 'DottedDataList',
            subType: '',
            items: [
              {
                name: 'Data Hosting Locations',
              },
            ],
          }
          _detailsItems.push(item)
        }
      }
      if (_detailsItems.length > 0) {
        setDetailsItems((prev) => [...prev, ..._detailsItems])
      }
    }, [vendor, securityData, securityLoading])

    useEffect(() => {
      if (THEME !== 'ilta') {
        loadContents({
          variables: {
            slug: vendor?.slug || '',
          },
        })
      }
    }, [authInfo])

    const showWebsite =
      productType === 'graveyard' || (productType === 'consolidation' && consolidationData?.enableInActiveDir === false)
        ? false
        : true
    // eslint-disable-next-line no-console
    // console.log('details item', detailsItems)
    return (
      <div className={cn(className, 'flex flex-col justify-center w-full')}>
        {compareInfos.solutions && compareInfos.solutions.length ? (
          <Drawer duration={250} hideScrollbars={true} onClose={closeDrawer} isVisible={isVisible}>
            <CompareModalContent
              key={randomNum + '-Overview-CompareModalContent'}
              onClose={closeDrawer}
              solutionType={compareInfos.solutions[0].solutionType}
            />
          </Drawer>
        ) : null}
        <div className="flex flex-col justify-center">
          <div className="!max-w-[1100px] w-full flex md:flex-row flex-col py-2">
            <div
              key={randomNum + '-DetailContainer'}
              className={`flex flex-col ${similars.length > 0 || relatedContent?.length > 0 ? 'md:w-[50rem] md:mr-[2.3125rem]' : 'w-full'} w-full`}
            >
              <Section
                key={randomNum + '-Overview-Section'}
                title={'Overview'}
                shortDescription={''}
                description={longDescription || ''}
                url={!showWebsite ? '' : website || ''}
                items={overviewItems}
                unlockPaywalledContent={unlockPaywalledContent}
              />
              {featuresItems.length > 0 && (
                <Section
                  key={randomNum + '-Overview-Features'}
                  title={'Features'}
                  description={''}
                  url={''}
                  items={featuresItems}
                  unlockPaywalledContent={unlockPaywalledContent}
                />
              )}
              {areasItems.length > 0 && (
                <Section
                  key={randomNum + '-Overview-AreasofUse'}
                  title={'Areas of Use'}
                  description={''}
                  url={''}
                  items={areasItems}
                  unlockPaywalledContent={unlockPaywalledContent}
                />
              )}
              {detailsItems.length > 0 && (
                <Section
                  key={randomNum + '-Overview-Details'}
                  title={'Details'}
                  description={''}
                  url={''}
                  items={detailsItems}
                  renderItem={(founder) => (
                    <SolutionFounderItem key={randomNum + '-Overview-SolutionFounderItem'} founder={founder} />
                  )}
                  unlockPaywalledContent={unlockPaywalledContent}
                />
              )}
              {checkNotEmpty(vendor?.articles) && <ArticleCart articles={vendor?.articles || []} />}
            </div>

            {(similars.length > 0 || relatedContent?.length > 0) && (
              <div className="flex flex-col md:w-[15rem] w-full h-full mb-auto">
                <ThemeOnly theme="legaltech">
                  {relatedContent && (
                    <RelatedContents
                      contents={relatedContent}
                      slug={vendor?.slug ? 'vendors/' + vendor?.slug : ''}
                      totalRelatedContent={totalRelatedContent}
                    />
                  )}
                </ThemeOnly>

                {similars.length > 0 && (
                  <SimilarSolutions
                    key={randomNum + '-Overview-similars'}
                    similars={similars}
                    slug={vendor?.slug || ''}
                    originName={vendor?.displayName || ''}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  },
)
